import { useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { PhotoUpload, photoUploadErrorMessage } from '@/components/shared/PhotoUpload';
import { CropPhotoEditor } from '@/components/shared/CropPhotoEditor';
import {
  userManagementService,
  type UpdateUserPayload,
  type AdminPasswordReset,
} from '@/services/user-management.service';
import { departmentService } from '@/services/department.service';
import { rolesService } from '@/services/roles.service';
import {
  useIdentityAvailability,
  emailAvailabilityMessage,
  takenMessage,
} from './use-identity-availability';
import { SharedMailboxCard } from './SharedMailboxCard';
import { usePermissions } from '@/hooks/use-permissions';
import { TemporaryPasswordDialog } from './TemporaryPasswordDialog';
import { SkeletonPage } from '@/components/skeletons';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { PhoneInput } from '@/components/shared/PhoneInput';
import { toast, confirmDelete } from '@/lib/swal';
import { ROUTES } from '@/routes/routes';

const selectClass =
  'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50';

/** Renders an ISO timestamp in the viewer's locale, or an em dash when unset. */
function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  // PP-1 (PP-OD-6): the picked file goes through the crop editor first.
  const [pendingPhoto, setPendingPhoto] = useState<File | null>(null);

  const { data: user, isLoading } = useQuery({
    queryKey: ['org-user', id],
    queryFn: () => userManagementService.get(id!),
    enabled: !!id,
  });
  const { data: depts } = useQuery({
    queryKey: ['departments-all'],
    queryFn: departmentService.listAll,
  });
  const { data: roles } = useQuery({ queryKey: ['roles'], queryFn: rolesService.listAll });
  // pageSize must stay within the endpoint's cap (`user-list-query.dto.ts`:
  // `@Max(100)`). 200 made the request 400, leaving this picker permanently
  // empty with no visible sign that anything had failed.
  const { data: managers, isError: managersFailed } = useQuery({
    queryKey: ['users-all'],
    queryFn: () => userManagementService.list({ pageSize: 100, isActive: true }),
  });

  const [form, setForm] = useState<
    UpdateUserPayload & { departmentId: string; reportingManagerId: string }
  >({
    email: '',
    isSharedEmailAccount: false,
    firstName: '',
    lastName: '',
    username: '',
    phone: '',
    employeeCode: '',
    alternatePhone: '',
    alternateEmail: '',
    designation: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    notes: '',
    departmentId: '',
    reportingManagerId: '',
  });
  const [formInit, setFormInit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [rolesSaving, setRolesSaving] = useState(false);

  if (user && !formInit) {
    setForm({
      email: user.email ?? '',
      isSharedEmailAccount: user.isSharedEmailAccount,
      firstName: user.firstName,
      lastName: user.lastName,
      username: user.username ?? '',
      phone: user.phone ?? '',
      employeeCode: user.employeeCode ?? '',
      alternatePhone: user.alternatePhone ?? '',
      alternateEmail: user.alternateEmail ?? '',
      designation: user.designation ?? '',
      address: user.address ?? '',
      city: user.city ?? '',
      state: user.state ?? '',
      country: user.country ?? '',
      postalCode: user.postalCode ?? '',
      emergencyContactName: user.emergencyContactName ?? '',
      emergencyContactPhone: user.emergencyContactPhone ?? '',
      notes: user.notes ?? '',
      departmentId: user.department?.id ?? '',
      reportingManagerId: user.reportingManager?.id ?? '',
    });
    setRoleIds(user.roles.map((r) => r.id));
    setFormInit(true);
  }

  /**
   * D-100 — the same advisory checks the Add User form runs, with this user
   * excluded so re-saving their own unchanged value never reads as a conflict.
   *
   * Owner/Admin cannot hold a shared mailbox, so the checkbox is hidden for
   * them exactly as it is on create; `resolveShared()` forces false server-side
   * regardless, so the UI is a courtesy rather than the control.
   */
  const isPrivileged = (user?.roles ?? []).some((r) => r.slug === 'owner' || r.slug === 'admin');
  const emailState = useIdentityAvailability('email', form.email ?? '', {
    ...(id ? { excludeUserId: id } : {}),
    minLength: 3,
  });
  const usernameState = useIdentityAvailability('username', form.username ?? '', {
    ...(id ? { excludeUserId: id } : {}),
    minLength: 2,
  });
  const phoneState = useIdentityAvailability('phone', form.phone ?? '', {
    ...(id ? { excludeUserId: id } : {}),
    minLength: 6,
  });
  const employeeCodeState = useIdentityAvailability('employeeCode', form.employeeCode ?? '', {
    ...(id ? { excludeUserId: id } : {}),
    minLength: 2,
  });

  // Re-derived from the response already held when the checkbox flips — no
  // second request.
  const emailMessage = emailAvailabilityMessage(emailState, form.isSharedEmailAccount ?? false);
  const usernameTaken = takenMessage(usernameState, 'Username');
  const phoneTaken = takenMessage(phoneState, 'Mobile number');
  const employeeCodeTaken = takenMessage(employeeCodeState, 'Employee code');
  const inlineBlocked =
    emailMessage?.tone === 'error' || !!usernameTaken || !!phoneTaken || !!employeeCodeTaken;
  const inlineChecking =
    emailState.status === 'checking' ||
    usernameState.status === 'checking' ||
    phoneState.status === 'checking' ||
    employeeCodeState.status === 'checking';

  const updateMutation = useMutation({
    mutationFn: (payload: UpdateUserPayload) => userManagementService.update(id!, payload),
    onSuccess: () => {
      toast.success('User updated');
      queryClient.invalidateQueries({ queryKey: ['org-user', id] });
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
      // Completing a missing username/mobile/employee code removes this user
      // from the Incomplete Identity report. That list is a separate query, so
      // without this the admin saves, navigates back, and still sees the row
      // they just fixed.
      queryClient.invalidateQueries({ queryKey: ['users', 'incomplete-identity'] });
    },
    onError: () => toast.error('Failed to update user'),
  });

  const photoUploadMutation = useMutation({
    mutationFn: (file: File) => userManagementService.uploadPhoto(id!, file),
    onSuccess: () => {
      toast.success('Photo updated');
      queryClient.invalidateQueries({ queryKey: ['org-user', id] });
    },
    onError: (e) => toast.error(photoUploadErrorMessage(e)),
  });

  const photoDeleteMutation = useMutation({
    mutationFn: () => userManagementService.deletePhoto(id!),
    onSuccess: () => {
      toast.success('Photo removed');
      queryClient.invalidateQueries({ queryKey: ['org-user', id] });
    },
    onError: () => toast.error('Failed to remove photo'),
  });

  // ── Admin password reset (D-074 §6) ────────────────────────────────────────
  // The plaintext password comes back once and is held in component state only
  // for as long as the dialog is open — never persisted, never re-fetchable.
  const [resetResult, setResetResult] = useState<AdminPasswordReset | null>(null);
  const canResetPassword = has('user:reset-password');

  const resetPasswordMutation = useMutation({
    mutationFn: () => userManagementService.resetPassword(id!),
    onSuccess: (result) => {
      setResetResult(result);
      // The reset bumps tokenVersion and sets mustChangePassword, so the cached
      // row is stale the moment this returns. The detail query is keyed
      // ['org-user', id] — invalidating ['user', id] matched nothing and left
      // the Identity & Access panel showing the pre-reset password status.
      void queryClient.invalidateQueries({ queryKey: ['org-user', id] });
      void queryClient.invalidateQueries({ queryKey: ['org-users'] });
    },
    onError: (err) => {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      toast.error(
        status === 403
          ? 'You do not have permission to reset this account.'
          : 'Could not reset the password. Please try again.',
      );
    },
  });

  const set =
    (field: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((f) => ({
        ...f,
        [field]: field === 'username' ? e.target.value.toLowerCase() : e.target.value,
      }));

  const toggleRole = (roleId: string) =>
    setRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((r) => r !== roleId) : [...prev, roleId],
    );

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    // D-100 — advisory guard only; the backend still validates on write.
    if (inlineBlocked || inlineChecking) return;
    setSaving(true);
    try {
      await updateMutation.mutateAsync({
        firstName: form.firstName,
        lastName: form.lastName,
        username: (form.username ?? '').trim().toLowerCase() || undefined,
        phone: form.phone || undefined,
        alternatePhone: form.alternatePhone || undefined,
        alternateEmail: form.alternateEmail || undefined,
        designation: form.designation || undefined,
        address: form.address || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        country: form.country || undefined,
        postalCode: form.postalCode || undefined,
        emergencyContactName: form.emergencyContactName || undefined,
        emergencyContactPhone: form.emergencyContactPhone || undefined,
        notes: form.notes || undefined,
        departmentId: form.departmentId || null,
        reportingManagerId: form.reportingManagerId || null,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRoles = async () => {
    setRolesSaving(true);
    try {
      await userManagementService.assignRoles(id!, roleIds);
      toast.success('Roles updated');
      queryClient.invalidateQueries({ queryKey: ['org-user', id] });
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
    } catch {
      toast.error('Failed to update roles');
    } finally {
      setRolesSaving(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (await confirmDelete('profile photo')) photoDeleteMutation.mutate();
  };

  if (isLoading || !user) return <SkeletonPage />;

  return (
    <div className="space-y-6">
      {/* Back nav + header */}
      <div>
        <button
          onClick={() => navigate(ROUTES.ORG_USERS)}
          className="mb-3 flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
          Back to Users
        </button>
        <div className="flex items-start gap-4">
          {/* Avatar — PP-1 (PP-OD-6): the shared upload affordance and crop editor,
              id-scoped through userManagementService. Rank / owner / self rules
              are the server's; this page only mirrors the outcome. */}
          <PhotoUpload
            firstName={user.firstName}
            lastName={user.lastName}
            src={user.profilePhotoUrl}
            size="lg"
            inputId="admin-profile-photo-input"
            uploading={photoUploadMutation.isPending || photoDeleteMutation.isPending}
            onSelect={setPendingPhoto}
            onReject={(message) => toast.error(message)}
            onRemove={() => void handleDeletePhoto()}
          />
          <CropPhotoEditor
            file={pendingPhoto}
            open={pendingPhoto !== null}
            saving={photoUploadMutation.isPending}
            firstName={user.firstName}
            lastName={user.lastName}
            title={`Adjust ${user.firstName}'s photo`}
            onCancel={() => setPendingPhoto(null)}
            onConfirm={async (cropped) => {
              try {
                await photoUploadMutation.mutateAsync(cropped);
                setPendingPhoto(null);
                return true;
              } catch {
                return false; // the mutation's onError already explained
              }
            }}
          />
          <div className="pt-2">
            <h1 className="text-xl font-semibold text-foreground">
              {user.firstName} {user.lastName}
            </h1>
            <p className="text-sm text-muted-foreground">{user.email}</p>
            {user.employeeCode && (
              <p className="font-mono text-xs text-muted-foreground">{user.employeeCode}</p>
            )}
          </div>
        </div>
      </div>

      {/* Profile form */}
      <section className="rounded-lg border border-border bg-card">
        <div className="border-b border-border px-6 py-4">
          <h2 className="text-sm font-semibold text-foreground">Profile Information</h2>
        </div>
        <form onSubmit={handleSaveProfile} className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="First Name" htmlFor="d-fn" required>
              <Input
                id="d-fn"
                value={form.firstName}
                onChange={set('firstName')}
                disabled={saving}
              />
            </FormField>
            <FormField label="Last Name" htmlFor="d-ln" required>
              <Input id="d-ln" value={form.lastName} onChange={set('lastName')} disabled={saving} />
            </FormField>
          </div>
          <FormField
            label="Email"
            htmlFor="d-email"
            required={isPrivileged}
            error={emailMessage?.tone === 'error' ? emailMessage.text : undefined}
          >
            <Input
              id="d-email"
              type="email"
              value={form.email ?? ''}
              onChange={set('email')}
              disabled={saving}
            />
            {emailMessage?.tone === 'info' && (
              <p data-testid="email-shared-info" className="mt-1.5 text-xs text-muted-foreground">
                {emailMessage.text}
              </p>
            )}
          </FormField>
          {isPrivileged ? (
            <p className="-mt-2 text-xs text-muted-foreground">
              Owner and Admin accounts need their own email address, so it cannot be shared.
            </p>
          ) : (
            <label className="-mt-2 flex cursor-pointer items-start gap-2 text-sm">
              <input
                id="d-shared-email"
                type="checkbox"
                className="mt-0.5"
                checked={form.isSharedEmailAccount ?? false}
                onChange={(e) => setForm((f) => ({ ...f, isSharedEmailAccount: e.target.checked }))}
                disabled={saving}
              />
              <span>
                <span className="font-medium">Shared email account</span>
                <span className="block text-xs text-muted-foreground">
                  This address may be used by more than one employee. Each person still signs in
                  with their own username or mobile and their own password.
                </span>
              </span>
            </label>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Employee Code" htmlFor="d-emp" error={employeeCodeTaken ?? undefined}>
              <Input
                id="d-emp"
                value={form.employeeCode ?? ''}
                onChange={set('employeeCode')}
                disabled={saving}
              />
            </FormField>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              label="Username"
              htmlFor="d-uname"
              required
              error={usernameTaken ?? undefined}
            >
              <Input
                id="d-uname"
                value={form.username}
                onChange={set('username')}
                disabled={saving}
              />
            </FormField>
            <FormField label="Designation" htmlFor="d-desig">
              <Input
                id="d-desig"
                value={form.designation}
                onChange={set('designation')}
                disabled={saving}
              />
            </FormField>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Phone" htmlFor="d-phone" required error={phoneTaken ?? undefined}>
              <PhoneInput
                id="d-phone"
                value={form.phone ?? ''}
                onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
                disabled={saving}
              />
            </FormField>
            <FormField label="Alternate Phone" htmlFor="d-altphone">
              <PhoneInput
                id="d-altphone"
                value={form.alternatePhone ?? ''}
                onChange={(v) => setForm((f) => ({ ...f, alternatePhone: v }))}
                disabled={saving}
              />
            </FormField>
          </div>
          {/* S-2 — the server ends every session when a sign-in identifier changes. */}
          <p className="text-xs text-muted-foreground" data-testid="identifier-change-notice">
            Changing the email, username or phone signs this user out of every device; they will
            need to sign in again with the new details.
          </p>
          <FormField label="Alternate Email" htmlFor="d-altemail">
            <Input
              id="d-altemail"
              type="email"
              value={form.alternateEmail}
              onChange={set('alternateEmail')}
              disabled={saving}
            />
          </FormField>
          <FormField label="Department" htmlFor="d-dept">
            <select
              id="d-dept"
              value={form.departmentId}
              onChange={set('departmentId')}
              disabled={saving}
              className={selectClass}
            >
              <option value="">— None —</option>
              {depts
                ?.filter((d) => d.isActive)
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} ({d.code})
                  </option>
                ))}
            </select>
          </FormField>
          <FormField
            label="Reporting Manager"
            htmlFor="d-mgr"
            error={
              managersFailed
                ? 'Could not load the user list. Reload the page to try again.'
                : undefined
            }
          >
            <select
              id="d-mgr"
              value={form.reportingManagerId}
              onChange={set('reportingManagerId')}
              disabled={saving}
              className={selectClass}
            >
              <option value="">— None —</option>
              {managers?.items
                .filter((u) => u.id !== id)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} {u.lastName} ({u.email})
                  </option>
                ))}
            </select>
          </FormField>
          <FormField label="Address" htmlFor="d-addr">
            <Input id="d-addr" value={form.address} onChange={set('address')} disabled={saving} />
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="City" htmlFor="d-city">
              <Input id="d-city" value={form.city} onChange={set('city')} disabled={saving} />
            </FormField>
            <FormField label="State" htmlFor="d-state">
              <Input id="d-state" value={form.state} onChange={set('state')} disabled={saving} />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Country" htmlFor="d-country">
              <Input
                id="d-country"
                value={form.country}
                onChange={set('country')}
                disabled={saving}
              />
            </FormField>
            <FormField label="Postal Code" htmlFor="d-postal">
              <Input
                id="d-postal"
                value={form.postalCode}
                onChange={set('postalCode')}
                disabled={saving}
              />
            </FormField>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Emergency Contact Name" htmlFor="d-ecname">
              <Input
                id="d-ecname"
                value={form.emergencyContactName}
                onChange={set('emergencyContactName')}
                disabled={saving}
              />
            </FormField>
            <FormField label="Emergency Contact Phone" htmlFor="d-ecphone">
              <Input
                id="d-ecphone"
                value={form.emergencyContactPhone}
                onChange={set('emergencyContactPhone')}
                disabled={saving}
              />
            </FormField>
          </div>
          <FormField label="Notes" htmlFor="d-notes">
            <Textarea
              id="d-notes"
              value={form.notes}
              onChange={set('notes')}
              rows={3}
              disabled={saving}
            />
          </FormField>
          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={saving} className="min-w-[120px]">
              {saving ? (
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>
      </section>

      {/* Identity & Access — password status, activity, and admin reset (D-074) */}
      <section className="rounded-lg border border-border bg-card">
        <div className="border-b border-border px-6 py-4">
          <h2 className="text-sm font-semibold text-foreground">Identity &amp; Access</h2>
        </div>
        <div className="px-6 py-5 space-y-5">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt className="text-xs font-medium text-muted-foreground">Password Status</dt>
              <dd className="mt-1 text-sm">
                {user.mustChangePassword ? (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                    Must change at next sign-in
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                    Active
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted-foreground">Account Status</dt>
              <dd className="mt-1 text-sm text-foreground">
                {user.isActive ? 'Active' : 'Suspended'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted-foreground">Last Login</dt>
              <dd className="mt-1 text-sm text-foreground">{formatDateTime(user.lastLoginAt)}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted-foreground">Last Password Change</dt>
              <dd className="mt-1 text-sm text-foreground">
                {formatDateTime(user.passwordChangedAt)}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted-foreground">Username</dt>
              <dd className="mt-1 font-mono text-sm text-foreground">{user.username ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted-foreground">Employee Code</dt>
              <dd className="mt-1 font-mono text-sm text-foreground">{user.employeeCode ?? '—'}</dd>
            </div>
          </dl>

          {canResetPassword && (
            <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-muted-foreground">
                Issues a temporary password and signs this employee out of all active sessions.
              </p>
              <Button
                type="button"
                variant="secondary"
                onClick={() => resetPasswordMutation.mutate()}
                disabled={resetPasswordMutation.isPending}
                className="min-h-[44px] shrink-0"
              >
                {resetPasswordMutation.isPending ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                ) : (
                  'Reset Password'
                )}
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* D-100 — who else uses this address. Renders nothing for a normal account. */}
      <SharedMailboxCard
        userId={id!}
        email={user.email}
        isSharedEmailAccount={user.isSharedEmailAccount}
      />

      {/* Roles section */}
      <section className="rounded-lg border border-border bg-card">
        <div className="border-b border-border px-6 py-4">
          <h2 className="text-sm font-semibold text-foreground">Role Assignments</h2>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="flex flex-wrap gap-2">
            {roles?.map((r) => (
              <label
                key={r.id}
                className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${roleIds.includes(r.id) ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={roleIds.includes(r.id)}
                  onChange={() => toggleRole(r.id)}
                  disabled={rolesSaving}
                />
                {r.name}
              </label>
            ))}
          </div>
          <div className="flex justify-end">
            <Button onClick={handleSaveRoles} disabled={rolesSaving} className="min-w-[120px]">
              {rolesSaving ? (
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
              ) : (
                'Update Roles'
              )}
            </Button>
          </div>
        </div>
      </section>

      <TemporaryPasswordDialog
        result={resetResult}
        employeeName={`${user.firstName} ${user.lastName}`.trim()}
        onClose={() => setResetResult(null)}
      />
    </div>
  );
}
